// src/hooks/useHideOnScroll.ts
import { useEffect, useRef, useState } from "react";

/**
 * Hides on scroll down, shows on scroll up.
 * Matches your existing behavior:
 * - topAlwaysShow: when scrollY <= 0, always shows
 * - threshold: ignores tiny movements (default 6)
 */
export default function useHideOnScroll(options?: {
  threshold?: number;
  topAlwaysShow?: boolean;
}) {
  const { threshold = 6, topAlwaysShow = true } = options || {};
  const [hidden, setHidden] = useState(false);

  const lastYRef = useRef(0);
  const tickingRef = useRef(false);

  useEffect(() => {
    lastYRef.current = window.scrollY || 0;

    const onScroll = () => {
      const currentY = window.scrollY || 0;
      if (tickingRef.current) return;

      tickingRef.current = true;
      requestAnimationFrame(() => {
        const delta = currentY - lastYRef.current;

        if (topAlwaysShow && currentY <= 0) {
          setHidden(false);
        } else if (delta > threshold) {
          // scrolling down
          setHidden(true);
        } else if (delta < -threshold) {
          // scrolling up
          setHidden(false);
        }

        lastYRef.current = currentY;
        tickingRef.current = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold, topAlwaysShow]);

  return { hidden };
}
